import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Fade,
  Stack,
  Tooltip,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Grow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  FormLabel,
  FormGroup,
  FormControlLabel,
  ListItemText,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Videocam as VideocamIcon,
  Campaign as CampaignIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import AnunciosService, {
  Anuncio,
  AnuncioCreate,
  AnuncioUpdate,
  AnuncioFilter,
} from '../../services/anunciosService';
import EmissorasService from '../../services/emissorasService';
import MarcasService from '../../services/marcasService';
import ItensService from '../../services/itensService';
import AnunciantesService from '../../services/anunciantesService';
import SetoresService from '../../services/setoresService';
import CategoriasService from '../../services/categoriasService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

/** Emissora no seletor. `studio_id` decide se o monitoramento é possível:
 *  sem ele o audio-matcher ignora a emissora sem emitir erro nenhum. */
interface EmissoraOpcao extends EntidadeSimples {
  meio?: string;
  cidade?: string;
  estado?: string;
  studio_id?: number | null;
}

/** Filtro por substring, insensível a caixa — mesmo comportamento dos
 *  Autocomplete do cadastro de registro. */
const filtrarPorNome = <T extends { nome?: string }>(opcoes: T[], texto: string): T[] => {
  const termo = (texto || '').toLowerCase().trim();
  if (!termo) return opcoes;
  return opcoes.filter((o) => (o.nome || '').toLowerCase().includes(termo));
};

/** Rótulo da emissora: praça e meio desambiguam nomes repetidos, e o aviso de
 *  "sem monitoramento" evita a escolha de uma emissora que o audio-matcher vai
 *  ignorar em silêncio, por não ter studio_id. */
const rotuloEmissora = (e: EmissoraOpcao) => {
  const praca = [e.cidade, e.estado].filter(Boolean).join('/');
  const detalhes = [e.meio, praca].filter(Boolean).join(' · ');
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <span>{e.nome}</span>
      {detalhes && (
        <Typography component="span" variant="caption" color="textSecondary">
          {detalhes}
        </Typography>
      )}
      {!e.studio_id && (
        <Typography component="span" variant="caption" color="error">
          (sem monitoramento)
        </Typography>
      )}
    </Box>
  );
};

interface SeletorEmissorasProps {
  opcoes: EmissoraOpcao[];
  selecionadas: number[];
  onChange: (ids: number[]) => void;
  carregando?: boolean;
}

/** Seleção múltipla de emissoras com busca — no lugar da lista de checkboxes,
 *  impraticável com 213 emissoras. Busca também por cidade, estado e meio. */
const SeletorEmissoras: React.FC<SeletorEmissorasProps> = ({
  opcoes, selecionadas, onChange, carregando,
}) => {
  const semVinculo = opcoes.filter((o) => selecionadas.includes(o.id) && !o.studio_id).length;
  return (
    <>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={opcoes}
        getOptionLabel={(o) => o.nome || ''}
        value={opcoes.filter((o) => selecionadas.includes(o.id))}
        onChange={(_, novo) => onChange(novo.map((o) => o.id))}
        isOptionEqualToValue={(o, v) => o.id === v?.id}
        filterOptions={(options, state) => {
          const termo = state.inputValue.toLowerCase().trim();
          if (!termo) return options;
          return options.filter((o) =>
            [o.nome, o.cidade, o.estado, o.meio]
              .filter(Boolean).join(' ').toLowerCase().includes(termo)
          );
        }}
        loading={carregando}
        noOptionsText="Nenhuma emissora encontrada"
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
            {rotuloEmissora(option)}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              size="small"
              label={option.nome}
              color={option.studio_id ? 'default' : 'error'}
              variant={option.studio_id ? 'filled' : 'outlined'}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Emissoras *"
            placeholder="Buscar por nome, cidade ou estado..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {carregando ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {semVinculo > 0 && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {semVinculo} emissora(s) selecionada(s) sem vínculo de monitoramento — o anúncio
          não será procurado nelas.
        </Typography>
      )}
    </>
  );
};

interface SeletorEntidadeProps {
  label: string;
  opcoes: EntidadeSimples[];
  valor?: number | string | null;
  onChange: (id: number | undefined) => void;
  obrigatorio?: boolean;
  carregando?: boolean;
}

/** Dropdown de entidade com busca digitável. Substitui o <Select> simples, que
 *  ficava impraticável com milhares de marcas/itens. */
const SeletorEntidade: React.FC<SeletorEntidadeProps> = ({
  label, opcoes, valor, onChange, obrigatorio, carregando,
}) => (
  <Autocomplete
    options={opcoes}
    getOptionLabel={(o) => o.nome || ''}
    value={opcoes.find((o) => o.id === valor) || null}
    onChange={(_, novo) => onChange(novo?.id)}
    isOptionEqualToValue={(o, v) => o.id === v?.id}
    filterOptions={(options, state) => filtrarPorNome(options, state.inputValue)}
    loading={carregando}
    noOptionsText="Nenhum resultado"
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        required={obrigatorio}
        placeholder="Digite para buscar..."
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {carregando ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  />
);

const AnunciosPage: React.FC = () => {
  const theme = useTheme();
  // Estado de listagem
  const [anuncios, setAnuncios] = useState<Anuncio[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [marcas, setMarcas] = useState<EntidadeSimples[]>([]);
  const [itens, setItens] = useState<EntidadeSimples[]>([]);
  const [anunciantes, setAnunciantes] = useState<EntidadeSimples[]>([]);
  const [setores, setSetores] = useState<EntidadeSimples[]>([]);
  const [categorias, setCategorias] = useState<EntidadeSimples[]>([]);
  const [emissoras, setEmissoras] = useState<EmissoraOpcao[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<AnuncioFilter>({});
  
  // Lista fixa de tipos
  const tipos = ["Comercial", "Promocional", "Institucional", "Varejo"];

  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentAnuncio, setCurrentAnuncio] = useState<Anuncio | null>(null);
  const [newAnuncio, setNewAnuncio] = useState<AnuncioCreate>({ nome: '' });
  const [editAnuncio, setEditAnuncio] = useState<AnuncioUpdate>({});
  // Liga/desliga o monitoramento sem apagar o anúncio nem mexer na vigência.
  const [newAnuncioAtivo, setNewAnuncioAtivo] = useState<boolean>(true);
  const [editAnuncioAtivo, setEditAnuncioAtivo] = useState<boolean>(true);

  // Estado para arquivos
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [editAudioFile, setEditAudioFile] = useState<File | null>(null);

  // Estado para emissoras selecionadas
  const [selectedEmissoras, setSelectedEmissoras] = useState<number[]>([]);
  const [editSelectedEmissoras, setEditSelectedEmissoras] = useState<number[]>([]);

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      // Dados reais. Antes eram mocks ('Marca 1', 'Emissora 1'...), o que fazia o
      // formulário gravar marca_id/item_id/setor_id arbitrários.
      const [emissorasData, marcasData, itensData, anunciantesData, setoresData, categoriasData] =
        await Promise.all([
          EmissorasService.getEmissoras({}, 0, 1000),
          MarcasService.getMarcas({}, 0, 1000),
          ItensService.getItens({}, 0, 1000),
          AnunciantesService.getAnunciantes({}, 0, 1000),
          SetoresService.getSetores({}, 0, 1000),
          CategoriasService.getCategorias({}, 0, 1000),
        ]);

      // Monitoráveis primeiro (com studio_id), depois alfabético.
      setEmissoras(
        (emissorasData || [])
          .map((e: any) => ({
            id: e.id,
            nome: e.nome,
            meio: e.meio,
            cidade: e.cidade,
            estado: e.estado,
            studio_id: e.studio_id ?? null,
          }))
          .sort((a: EmissoraOpcao, b: EmissoraOpcao) => {
            const pa = a.studio_id ? 0 : 1;
            const pb = b.studio_id ? 0 : 1;
            return pa !== pb ? pa - pb : (a.nome || '').localeCompare(b.nome || '');
          })
      );
      setMarcas((marcasData || []).map((m: any) => ({ id: m.id, nome: m.nome })));
      setItens((itensData || []).map((i: any) => ({ id: i.id, nome: i.nome })));
      setAnunciantes((anunciantesData || []).map((a: any) => ({ id: a.id, nome: a.nome })));
      setSetores((setoresData || []).map((s: any) => ({ id: s.id, nome: s.nome })));
      setCategorias((categoriasData || []).map((c: any) => ({ id: c.id, nome: c.nome })));
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchAnuncios = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      // Aqui o filtro 'nome' será utilizado para buscar em múltiplos campos no backend
      const data = await AnunciosService.getAnuncios(filter, skip, limit);
      setAnuncios(data);
      // Obs: Em uma API real, geralmente teríamos um count total para paginação
      // Mas vamos assumir que temos 100 registros para simplificar este exemplo
      setTotalCount(100);
    } catch (error) {
      console.error('Erro ao buscar anúncios:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar anúncios',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar anúncios ao iniciar a página ou mudar filtros/paginação
    fetchAnuncios();
    // Carregar referências
    carregarReferencias();
  }, [fetchAnuncios, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof AnuncioFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Adicionar função para limpar filtro
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateAnuncio = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newAnuncio.nome || !newAnuncio.titulo || !newAnuncio.marca_id || 
          !newAnuncio.item_id || !newAnuncio.iniciovigencia || 
          selectedEmissoras.length === 0 || !audioFile) {
        setSnackbar({
          open: true,
          message: 'Preencha todos os campos obrigatórios',
          severity: 'error',
        });
        return;
      }

      // O backend sincroniza anuncio_emissora a partir de `emissoras`.
      const anuncioData = {
        ...newAnuncio,
        emissoras: selectedEmissoras,
        ativo: newAnuncioAtivo,
      };

      const criado = await AnunciosService.createAnuncio(anuncioData);

      // O áudio só pode subir depois que o anúncio existe (a chave do S3 usa o id).
      // Se o upload falhar, o anúncio permanece criado e o usuário é avisado — sem
      // isso o comercial nunca vira fingerprint e nunca é procurado.
      let avisoAudio = '';
      if (audioFile && criado?.id) {
        try {
          await AnunciosService.uploadAudio(criado.id, audioFile);
        } catch (err) {
          console.error('Erro no upload do áudio:', err);
          avisoAudio = ' — mas o áudio NÃO foi enviado, use Editar para tentar de novo';
        }
      }

      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Anúncio criado com sucesso' + avisoAudio,
        severity: avisoAudio ? 'error' : 'success',
      });
      // Limpar formulário
      setNewAnuncio({ nome: '' });
      setSelectedEmissoras([]);
      setAudioFile(null);
      setNewAnuncioAtivo(true);
      // Recarregar dados
      fetchAnuncios();
    } catch (error) {
      console.error('Erro ao criar anúncio:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar anúncio',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (anuncio: Anuncio) => {
    setCurrentAnuncio(anuncio);
    // Inicializar dados para edição
    setEditAnuncio({
      nome: anuncio.nome,
      tipo: anuncio.tipo,
      titulo: anuncio.titulo,
      duracao: anuncio.duracao,
      mercado: anuncio.mercado,
      marca_id: anuncio.marca_id,
      item_id: anuncio.item_id,
      anunciante_id: anuncio.anunciante_id,
      setor_id: anuncio.setor_id,
      categoria_id: anuncio.categoria_id,
      programa_id: anuncio.programa_id,
      iniciovigencia: anuncio.iniciovigencia,
      fimvigencia: anuncio.fimvigencia,
      palavraschave: anuncio.palavraschave,
      amazonpath: anuncio.amazonpath
    });
    
    // Vínculos atuais vêm no próprio anúncio (emissora_ids). Sem isso, salvar a
    // edição apagaria os vínculos existentes por enviar lista vazia.
    setEditSelectedEmissoras(anuncio.emissora_ids || []);
    setEditAnuncioAtivo(anuncio.ativo !== false);

    setOpenEditDialog(true);
  };

  const handleEditAnuncio = async () => {
    if (!currentAnuncio) return;

    try {
      // Verificar campos obrigatórios
      if (!editAnuncio.nome || !editAnuncio.titulo || !editAnuncio.marca_id || 
          !editAnuncio.item_id || !editAnuncio.iniciovigencia || 
          editSelectedEmissoras.length === 0) {
        setSnackbar({
          open: true,
          message: 'Preencha todos os campos obrigatórios',
          severity: 'error',
        });
        return;
      }

      const anuncioData = {
        ...editAnuncio,
        emissoras: editSelectedEmissoras,
        ativo: editAnuncioAtivo,
      };

      await AnunciosService.updateAnuncio(currentAnuncio.id, anuncioData);

      // Áudio novo é adicional: o anúncio pode ter várias versões do comercial,
      // cada uma com seu próprio fingerprint.
      let avisoAudio = '';
      if (editAudioFile) {
        try {
          await AnunciosService.uploadAudio(currentAnuncio.id, editAudioFile);
        } catch (err) {
          console.error('Erro no upload do áudio:', err);
          avisoAudio = ' — mas o áudio NÃO foi enviado';
        }
      }

      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Anúncio atualizado com sucesso' + avisoAudio,
        severity: avisoAudio ? 'error' : 'success',
      });
      // Limpar estados
      setCurrentAnuncio(null);
      setEditAnuncio({});
      setEditSelectedEmissoras([]);
      setEditAudioFile(null);
      // Recarregar dados
      fetchAnuncios();
    } catch (error) {
      console.error('Erro ao atualizar anúncio:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar anúncio',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (anuncio: Anuncio) => {
    setCurrentAnuncio(anuncio);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAnuncio = async () => {
    if (!currentAnuncio) return;

    try {
      await AnunciosService.deleteAnuncio(currentAnuncio.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Anúncio removido com sucesso',
        severity: 'success',
      });
      // Limpar estado
      setCurrentAnuncio(null);
      // Recarregar dados
      fetchAnuncios();
    } catch (error) {
      console.error('Erro ao remover anúncio:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao remover anúncio',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangeNewAnuncio = (field: string, value: any) => {
    setNewAnuncio({ ...newAnuncio, [field]: value });
  };

  const handleChangeEditAnuncio = (field: string, value: any) => {
    setEditAnuncio({ ...editAnuncio, [field]: value });
  };

  const handleAudioFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setAudioFile(event.target.files[0]);
    }
  };

  const handleEditAudioFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setEditAudioFile(event.target.files[0]);
    }
  };

  const formatarData = (data: string | undefined) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarDuracao = (duracao: number | undefined) => {
    if (!duracao) return '-';
    const minutos = Math.floor(duracao / 60);
    const segundos = duracao % 60;
    return `${minutos}:${segundos < 10 ? '0' : ''}${segundos}`;
  };

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Anúncios
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Gerencie os anúncios registrados no sistema
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={10}>
            <TextField
              fullWidth
              label="Buscar por nome, título, marca ou item"
              variant="outlined"
              size="small"
              value={filter.nome || ''}
              onChange={(e) => handleFilterChange('nome', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: filter.nome ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="limpar busca"
                      onClick={clearSearchFilter}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              color="primary"
              sx={{ 
                textTransform: 'none',
                fontWeight: 'medium',
                boxShadow: 2
              }}
            >
              Novo
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de anúncios */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        // Visualização em lista (tabela)
        <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{
              [`& .${tableCellClasses.root}`]: {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }
            }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Marca</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Vigência</TableCell>
                  <TableCell>Duração</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anuncios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Nenhum anúncio encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  anuncios.map((anuncio) => (
                    <TableRow key={anuncio.id}>
                      <TableCell>{anuncio.nome}</TableCell>
                      <TableCell>{anuncio.tipo || '-'}</TableCell>
                      <TableCell>{anuncio.titulo || '-'}</TableCell>
                      <TableCell>{anuncio.marca?.nome || '-'}</TableCell>
                      <TableCell>{anuncio.item?.nome || '-'}</TableCell>
                      <TableCell>
                        {anuncio.iniciovigencia ? (
                          <Tooltip title="Período de vigência">
                            <span>
                              {formatarData(anuncio.iniciovigencia)}
                              {anuncio.fimvigencia ? ` - ${formatarData(anuncio.fimvigencia)}` : ''}
                            </span>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{formatarDuracao(anuncio.duracao)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(anuncio)}
                          title="Editar"
                          sx={{ 
                            mx: 0.5,
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(anuncio)}
                          title="Excluir"
                          sx={{ 
                            mx: 0.5,
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      )}

      {/* Dialog de Criação */}
      <Dialog 
        open={openCreateDialog} 
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          pb: 2
        }}>
          Novo Anúncio
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Informações Básicas */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Informações Básicas
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do Anúncio *"
                  required
                  value={newAnuncio.nome || ''}
                  onChange={(e) => handleChangeNewAnuncio('nome', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Título *"
                  required
                  value={newAnuncio.titulo || ''}
                  onChange={(e) => handleChangeNewAnuncio('titulo', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Início da Vigência *"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={newAnuncio.iniciovigencia || ''}
                  onChange={(e) => handleChangeNewAnuncio('iniciovigencia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fim da Vigência"
                  InputLabelProps={{ shrink: true }}
                  value={newAnuncio.fimvigencia || ''}
                  onChange={(e) => handleChangeNewAnuncio('fimvigencia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duração (em segundos)"
                  type="number"
                  value={newAnuncio.duracao || ''}
                  onChange={(e) => handleChangeNewAnuncio('duracao', parseInt(e.target.value, 10))}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mercado"
                  value={newAnuncio.mercado || ''}
                  onChange={(e) => handleChangeNewAnuncio('mercado', e.target.value)}
                />
              </Grid>

              {/* Classificações */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Classificações
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <SeletorEntidade
                    label="Marca *"
                    opcoes={marcas}
                    valor={newAnuncio.marca_id}
                    onChange={(id) => handleChangeNewAnuncio('marca_id', id)}
                    obrigatorio
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <SeletorEntidade
                    label="Item *"
                    opcoes={itens}
                    valor={newAnuncio.item_id}
                    onChange={(id) => handleChangeNewAnuncio('item_id', id)}
                    obrigatorio
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={newAnuncio.tipo || ''}
                    onChange={(e) => handleChangeNewAnuncio('tipo', e.target.value)}
                  >
                    {tipos.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>
                        {tipo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Anunciante"
                    opcoes={anunciantes}
                    valor={newAnuncio.anunciante_id}
                    onChange={(id) => handleChangeNewAnuncio('anunciante_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Setor"
                    opcoes={setores}
                    valor={newAnuncio.setor_id}
                    onChange={(id) => handleChangeNewAnuncio('setor_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Categoria"
                    opcoes={categorias}
                    valor={newAnuncio.categoria_id}
                    onChange={(id) => handleChangeNewAnuncio('categoria_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Palavras-chave"
                  value={newAnuncio.palavraschave || ''}
                  onChange={(e) => handleChangeNewAnuncio('palavraschave', e.target.value)}
                  helperText="Separe as palavras-chave por vírgula"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAnuncioAtivo}
                      onChange={(e) => setNewAnuncioAtivo(e.target.checked)}
                    />
                  }
                  label="Ativo (monitorar este anúncio)"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Desativado, o anúncio deixa de ser procurado nas gravações sem ser apagado.
                </Typography>
              </Grid>

              {/* Associações e Mídia */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Associações e Mídia
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <SeletorEmissoras
                    opcoes={emissoras}
                    selecionadas={selectedEmissoras}
                    onChange={setSelectedEmissoras}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <FormLabel>Arquivo de Áudio *</FormLabel>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Escolher Arquivo
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={handleAudioFileChange}
                      />
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {audioFile ? audioFile.name : 'Nenhum arquivo selecionado'}
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button 
            onClick={() => setOpenCreateDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleCreateAnuncio}
            disabled={!newAnuncio.nome}
            sx={{ textTransform: 'none', fontWeight: 'medium', ml: 1 }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Edição */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          pb: 2
        }}>
          Editar Anúncio
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Informações Básicas */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Informações Básicas
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do Anúncio *"
                  required
                  value={editAnuncio.nome || ''}
                  onChange={(e) => handleChangeEditAnuncio('nome', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Título *"
                  required
                  value={editAnuncio.titulo || ''}
                  onChange={(e) => handleChangeEditAnuncio('titulo', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Início da Vigência *"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={editAnuncio.iniciovigencia || ''}
                  onChange={(e) => handleChangeEditAnuncio('iniciovigencia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fim da Vigência"
                  InputLabelProps={{ shrink: true }}
                  value={editAnuncio.fimvigencia || ''}
                  onChange={(e) => handleChangeEditAnuncio('fimvigencia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duração (em segundos)"
                  type="number"
                  value={editAnuncio.duracao || ''}
                  onChange={(e) => handleChangeEditAnuncio('duracao', parseInt(e.target.value, 10))}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mercado"
                  value={editAnuncio.mercado || ''}
                  onChange={(e) => handleChangeEditAnuncio('mercado', e.target.value)}
                />
              </Grid>

              {/* Classificações */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Classificações
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <SeletorEntidade
                    label="Marca *"
                    opcoes={marcas}
                    valor={editAnuncio.marca_id}
                    onChange={(id) => handleChangeEditAnuncio('marca_id', id)}
                    obrigatorio
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <SeletorEntidade
                    label="Item *"
                    opcoes={itens}
                    valor={editAnuncio.item_id}
                    onChange={(id) => handleChangeEditAnuncio('item_id', id)}
                    obrigatorio
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={editAnuncio.tipo || ''}
                    onChange={(e) => handleChangeEditAnuncio('tipo', e.target.value)}
                  >
                    {tipos.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>
                        {tipo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Anunciante"
                    opcoes={anunciantes}
                    valor={editAnuncio.anunciante_id}
                    onChange={(id) => handleChangeEditAnuncio('anunciante_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Setor"
                    opcoes={setores}
                    valor={editAnuncio.setor_id}
                    onChange={(id) => handleChangeEditAnuncio('setor_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <SeletorEntidade
                    label="Categoria"
                    opcoes={categorias}
                    valor={editAnuncio.categoria_id}
                    onChange={(id) => handleChangeEditAnuncio('categoria_id', id)}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Palavras-chave"
                  value={editAnuncio.palavraschave || ''}
                  onChange={(e) => handleChangeEditAnuncio('palavraschave', e.target.value)}
                  helperText="Separe as palavras-chave por vírgula"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editAnuncioAtivo}
                      onChange={(e) => setEditAnuncioAtivo(e.target.checked)}
                    />
                  }
                  label="Ativo (monitorar este anúncio)"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Desativado, o anúncio deixa de ser procurado nas gravações sem ser apagado.
                </Typography>
              </Grid>

              {/* Associações e Mídia */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Associações e Mídia
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <SeletorEmissoras
                    opcoes={emissoras}
                    selecionadas={editSelectedEmissoras}
                    onChange={setEditSelectedEmissoras}
                    carregando={loadingReferencias}
                  />
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel>Arquivo de Áudio {!editAnuncio.amazonpath && '*'}</FormLabel>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {editAnuncio.amazonpath ? 'Alterar Arquivo' : 'Escolher Arquivo'}
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={handleEditAudioFileChange}
                      />
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {editAudioFile 
                        ? editAudioFile.name 
                        : (editAnuncio.amazonpath 
                          ? editAnuncio.amazonpath.split('/').pop() 
                          : 'Nenhum arquivo associado')}
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button 
            onClick={() => setOpenEditDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleEditAnuncio}
            disabled={!editAnuncio.nome}
            sx={{ textTransform: 'none', fontWeight: 'medium', ml: 1 }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          pb: 2
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Deseja realmente excluir o anúncio "{currentAnuncio?.nome}"?
          </Typography>
          <Typography variant="caption" color="error">
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteAnuncio}
            sx={{ textTransform: 'none', fontWeight: 'medium', ml: 1 }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default AnunciosPage; 